// src/components/Signup/SuccessStep.jsx

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { BiCheckCircle } from 'react-icons/bi';
import { AiOutlineLoading } from 'react-icons/ai';
import Button from '../Button';
import { registerBrokerAgent, cleanupAdminSession } from '../../api/api'; // ✅ Re-enable cleanup import

const SuccessStep = ({ formData, onBack }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    completeRegistration();
  }, []);

  const translateError = (errorMessage) => {
    if (!errorMessage) return t('signup.errors.unexpectedError');
    
    const lowerError = errorMessage.toLowerCase();
    
    if (lowerError.includes('network') || lowerError.includes('connection')) {
      return t('signup.errors.networkError');
    }
    
    if (lowerError.includes('server') || lowerError.includes('500') || lowerError.includes('503')) {
      return t('signup.errors.serverError');
    }
    
    if (lowerError.includes('registration') && lowerError.includes('failed')) {
      return t('signup.errors.registrationFailed');
    }
    
    return errorMessage;
  };

  const completeRegistration = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare registration data with current language
      const registrationData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        selectedLanguage: i18n.language,
        brokerAgentContactId: formData.brokerAgentContactId // Include verified contact ID
      };
      
      console.log('Registration data:', registrationData);
      
      // Call the real API (no admin auth needed for registration)
      const result = await registerBrokerAgent(registrationData);
      
      if (result.isSuccess) {
        setRegistrationComplete(true);
        console.log('Registration successful:', result.message);
      } else {
        throw new Error(result.message || t('signup.errors.registrationFailed'));
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      const translatedError = translateError(err.message);
      setError(translatedError);
      // ✅ Cleanup admin session on error
      await cleanupAdminSession();
    } finally {
      setLoading(false);
    }
  };

  // ... rest of the component remains the same
  const handleAccessPortal = () => {
    window.location.href = '/';
  };

  const handleRetry = () => {
    setError(null);
    completeRegistration();
  };

  const handleGoToLogin = () => {
    window.location.href = '/';
  };

  const handleBackToSignup = () => {
    if (onBack) {
      onBack();
    } else {
      window.location.href = '/signup';
    }
  };

  // ... rest of the component (Loading, Error, Success states) remains exactly the same as before
  
  // Loading State
  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <AiOutlineLoading className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('signup.success.creatingAccount')}
          </h2>
          <p className="text-gray-600">
            {t('signup.success.pleaseWait')}
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <div className="text-4xl text-red-500 mx-auto mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('signup.success.registrationError')}
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button
              variant="block"
              onClick={handleRetry}
              label={t('signup.success.tryAgain')}
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleBackToSignup}
                className="w-full px-4 py-2 border bg-white border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('signup.success.backToSignup')}
              </button>
              <button
                onClick={handleGoToLogin}
                className="w-full px-4 py-2 border bg-white border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('signup.success.goToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (registrationComplete) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <div className="relative mb-6">
            <BiCheckCircle className="text-6xl text-green-500 mx-auto" />
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full animate-ping"></div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {t('signup.success.header')}
          </h2>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-green-800">
              {t('signup.success.message')}
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-800 mb-2">
              {t('signup.success.accountCreatedFor')}
            </h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p>
                <strong>{t('signup.success.name')}:</strong> {formData.firstName} {formData.lastName}
              </p>
              <p>
                <strong>{t('signup.success.email')}:</strong> {formData.email}
              </p>
              <p>
                <strong>{t('signup.success.language')}:</strong> {
                  i18n.language === 'en' 
                    ? t('signup.success.english') 
                    : t('signup.success.french')
                }
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <div className="flex items-start">
              <span className="text-blue-500 mr-2">📧</span>
              <div className="text-left">
                <p className="text-sm text-blue-800 font-medium">
                  {t('signup.success.checkYourEmail')}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {t('signup.success.confirmationEmailSent')} <strong>{formData.email}</strong>
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            onClick={handleAccessPortal}
            label={t('signup.success.accessPortal')}
          />

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              {t('signup.success.loginInstructions')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

SuccessStep.propTypes = {
  formData: PropTypes.object.isRequired,
  onComplete: PropTypes.func,
  onBack: PropTypes.func
};

export default SuccessStep;